import { useMemo } from 'react';
import { useSelector } from '@xstate/react';
import { useQuery } from '@tanstack/react-query';
import { Selectors, SpaceActor } from '../actors/SpaceActor';
import { spacesQuery } from 'networking/mindfeed/queries/spaces';
import { boxesQuery } from 'networking/mindfeed/queries/boxes';
import { collectionsQuery } from 'networking/mindfeed/queries/collections';

/**
 * The user's current selection: which space and box, what the box is showing
 * (a view, or a collection), and whether the machine is still working it
 * out.
 *
 * The machine holds ids. The objects come from the React Query cache, so
 * they are the same ones the lists render and they update when the lists
 * do. They are undefined until the matching list is in the cache.
 */
export const useActiveSpace = () => {
  // State Helpers
  const isSelecting = useSelector(SpaceActor, Selectors.isSelecting);
  const isSelected = useSelector(SpaceActor, Selectors.isSelected);
  const isUnselected = useSelector(SpaceActor, Selectors.isUnselected);

  // Ids
  const selectedSpaceId = useSelector(SpaceActor, Selectors.selectedSpaceId);
  const selectedBoxId = useSelector(SpaceActor, Selectors.selectedBoxId);
  const selectedCollectionId = useSelector(
    SpaceActor,
    Selectors.selectedCollectionId,
  );

  // What the box is showing: one of its views, and the open Journal day.
  const selectedView = useSelector(SpaceActor, Selectors.selectedView);
  const journalDay = useSelector(SpaceActor, Selectors.journalDay);

  // The lists the objects are looked up in. Each one is only subscribed
  // once its id is known, so a page with no selection (or no signed-in
  // user) asks for nothing. Boxes and collections take the id directly and
  // skip themselves when it is missing; spaces has no id to key on, so it
  // is gated explicitly.

  const { data: spaces } = useQuery({
    ...spacesQuery(),
    enabled: selectedSpaceId !== undefined,
  });

  const { data: boxes } = useQuery(boxesQuery(selectedSpaceId));

  const { data: collections } = useQuery(collectionsQuery(selectedBoxId));

  // The objects: the entry of each list whose id is the selected one.

  const selectedSpace = useMemo(
    () => spaces?.find((space) => space.id === selectedSpaceId),
    [spaces, selectedSpaceId],
  );

  const selectedBox = useMemo(
    () => boxes?.find((box) => box.id === selectedBoxId),
    [boxes, selectedBoxId],
  );

  const selectedCollection = useMemo(
    () =>
      collections?.find((collection) => collection.id === selectedCollectionId),
    [collections, selectedCollectionId],
  );

  return {
    isSelecting,
    isSelected,
    isUnselected,

    selectedSpaceId,
    selectedBoxId,
    selectedCollectionId,
    selectedView,
    journalDay,

    selectedSpace,
    selectedBox,
    selectedCollection,
  };
};
